<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import draggable from 'vuedraggable';
import ClientPortalsAPI from 'dashboard/api/clientPortals';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WidgetRenderer from '../components/WidgetRenderer.vue';
import WidgetPropertiesPanel from '../components/WidgetPropertiesPanel.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Fonction pour générer un ID unique compatible
const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const loading = ref(true);
const saving = ref(false);
const previewMode = ref(false);
const portal = ref(null);
const dashboardConfig = ref({
  rows: [],
});
const selectedWidget = ref(null);
const selectedWidgetPath = ref(null);
const showProperties = ref(false);

const widgetTypes = [
  {
    type: 'text',
    label: 'Bloc Texte',
    icon: 'i-lucide-type',
    description: 'Ajoutez du texte formaté',
    defaultProps: {
      title: 'Titre du bloc',
      content: 'Contenu de votre bloc texte...',
      fontSize: 'base',
      alignment: 'left',
    },
  },
  {
    type: 'metric',
    label: 'Métrique',
    icon: 'i-lucide-activity',
    description: 'Affiche une valeur avec tendance',
    defaultProps: {
      title: 'Métrique',
      value: '1,234',
      subvalue: '+12%',
      trend: 'up',
      format: 'number',
    },
  },
  {
    type: 'chart',
    label: 'Graphique',
    icon: 'i-lucide-bar-chart',
    description: 'Graphique interactif',
    defaultProps: {
      title: 'Graphique',
      chartType: 'bar',
      series: [{ name: 'Série 1', data: [30, 40, 35, 50, 49, 60, 70] }],
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    },
  },
  {
    type: 'table',
    label: 'Tableau',
    icon: 'i-lucide-table',
    description: 'Tableau de données',
    defaultProps: {
      title: 'Tableau',
      headers: ['Colonne 1', 'Colonne 2', 'Colonne 3'],
      rows: [
        ['Donnée 1', 'Donnée 2', 'Donnée 3'],
        ['Donnée 4', 'Donnée 5', 'Donnée 6'],
      ],
    },
  },
  {
    type: 'conversations_list',
    label: 'Liste Conversations',
    icon: 'i-lucide-message-circle',
    description: 'Liste des conversations récentes',
    defaultProps: {
      title: 'Conversations',
      limit: 5,
      status: 'open',
    },
  },
  {
    type: 'contacts_list',
    label: 'Liste Contacts',
    icon: 'i-lucide-users',
    description: 'Liste des contacts récents',
    defaultProps: {
      title: 'Contacts',
      limit: 5,
    },
  },
  {
    type: 'inbox_status',
    label: 'Statut Inboxes',
    icon: 'i-lucide-mailbox',
    description: 'Statut des inboxes et canaux',
    defaultProps: {
      title: 'Statut des canaux',
    },
  },
  {
    type: 'stats_cards',
    label: 'Cartes Statistiques',
    icon: 'i-lucide-layout-grid',
    description: 'Plusieurs métriques en cartes',
    defaultProps: {
      title: 'Statistiques',
      cards: [
        { label: 'Conversations', value: '0', icon: 'i-lucide-message-square' },
        { label: 'Contacts', value: '0', icon: 'i-lucide-users' },
        { label: 'Non assignées', value: '0', icon: 'i-lucide-alert-circle' },
      ],
    },
  },
  {
    type: 'quick_actions',
    label: 'Actions Rapides',
    icon: 'i-lucide-zap',
    description: "Boutons d'actions rapides",
    defaultProps: {
      title: 'Actions rapides',
      actions: [
        {
          label: 'Nouvelle conversation',
          icon: 'i-lucide-plus',
          action: 'new_conversation',
        },
        {
          label: 'Nouveau contact',
          icon: 'i-lucide-user-plus',
          action: 'new_contact',
        },
      ],
    },
  },
  {
    type: 'drive',
    label: 'Drive',
    icon: 'i-lucide-hard-drive',
    description: 'Navigation dans les fichiers du Drive',
    defaultProps: {
      title: 'Drive',
      rootFolder: '/',
    },
  },
  {
    type: 'recent_activities',
    label: 'Activités Récentes',
    icon: 'i-lucide-activity',
    description: 'Liste des activités récentes',
    defaultProps: {
      title: 'Activités récentes',
      activities: [
        {
          type: 'message',
          title: 'Nouveau message reçu',
          description: 'Message de Jean Dupont',
          time: 'Il y a 5 min',
          icon: 'i-lucide-message-circle',
        },
        {
          type: 'update',
          title: 'Mise à jour du projet',
          description: 'Statut changé en "En cours"',
          time: 'Il y a 1h',
          icon: 'i-lucide-refresh-cw',
        },
      ],
    },
  },
  {
    type: 'timeline',
    label: 'Timeline',
    icon: 'i-lucide-calendar-clock',
    description: 'Chronologie des étapes',
    defaultProps: {
      title: 'Planning & Global Status',
      steps: [
        { label: 'Visite technique', status: 'completed', assignee: 'Tom' },
        { label: 'Offre & Devis', status: 'completed', assignee: 'Sarah' },
        { label: 'Travaux', status: 'current', assignee: 'Tom' },
        { label: 'Réception', status: 'pending' },
      ],
    },
  },
  {
    type: 'info_properties',
    label: 'Propriétés Info',
    icon: 'i-lucide-info',
    description: 'Liste de propriétés informatives',
    defaultProps: {
      title: 'Information',
      items: [
        {
          label: 'Adresse',
          value: '123 Rue de Paris',
          icon: 'i-lucide-map-pin',
        },
        { label: 'Gestionnaire', value: 'Jean Dupont', icon: 'i-lucide-user' },
        { label: 'Propriétaire', value: 'SCI Immo', icon: 'i-lucide-building' },
        { label: 'Exploitant', value: 'Logistics Ltd', icon: 'i-lucide-box' },
      ],
    },
  },
  {
    type: 'orders_list',
    label: 'Liste Commandes',
    icon: 'i-lucide-shopping-cart',
    description: 'Liste des commandes récentes',
    defaultProps: {
      title: 'Commandes',
      limit: 5,
    },
  },
  {
    type: 'map',
    label: 'Carte',
    icon: 'i-lucide-map',
    description: 'Carte géographique interactive',
    defaultProps: {
      title: 'Carte',
      region: 'france',
      pins: [
        {
          lat: 48.8566,
          lng: 2.3522,
          title: 'Paris',
          description: 'Capitale de la France',
          style: 'primary',
        },
      ],
    },
  },
  {
    type: 'delivery_status',
    label: 'Statut de Livraison',
    icon: 'i-lucide-truck',
    description: 'Statut des livraisons par état',
    defaultProps: {
      title: 'Statut de livraison',
    },
  },
  {
    type: 'chat',
    label: 'Chat',
    icon: 'i-lucide-message-square',
    description: 'Permet de chater avec des inboxes spécifiques',
    defaultProps: {
      title: 'Chat avec nous',
      inboxIds: [],
    },
  },
];

onMounted(async () => {
  try {
    const { data } = await ClientPortalsAPI.show(route.params.portalId);
    portal.value = data;
    if (data.config && data.config.dashboard && data.config.dashboard.rows) {
      dashboardConfig.value = data.config.dashboard;
    }
  } catch (error) {
    useAlert('Erreur lors du chargement du portail');
  } finally {
    loading.value = false;
  }
});

const addRow = () => {
  dashboardConfig.value.rows.push({
    id: generateId(),
    columns: [{ id: generateId(), widgets: [], width: 12 }],
  });
};

const addColumn = row => {
  const totalWidth = row.columns.reduce(
    (sum, col) => sum + (col.width || 12),
    0
  );
  const newWidth = Math.floor(12 / (row.columns.length + 1));
  row.columns.forEach(col => {
    col.width = newWidth;
  });
  row.columns.push({
    id: generateId(),
    widgets: [],
    width: newWidth,
  });
};

const removeRow = index => {
  if (confirm('Supprimer cette rangée ?')) {
    dashboardConfig.value.rows.splice(index, 1);
    deselectWidget();
  }
};

const removeColumn = (row, colIndex) => {
  if (row.columns.length <= 1) {
    useAlert('Au moins une colonne est requise');
    return;
  }
  if (confirm('Supprimer cette colonne ?')) {
    row.columns.splice(colIndex, 1);
    deselectWidget();
  }
};

const selectWidget = (widget, rowIndex, colIndex, widgetIndex) => {
  if (previewMode.value) return;
  selectedWidget.value = JSON.parse(JSON.stringify(widget));
  selectedWidgetPath.value = { rowIndex, colIndex, widgetIndex };
  showProperties.value = true;
};

const deselectWidget = () => {
  selectedWidget.value = null;
  selectedWidgetPath.value = null;
  showProperties.value = false;
};

const updateWidget = updatedWidget => {
  if (!selectedWidgetPath.value) return;
  const { rowIndex, colIndex, widgetIndex } = selectedWidgetPath.value;
  dashboardConfig.value.rows[rowIndex].columns[colIndex].widgets[widgetIndex] =
    updatedWidget;
  selectedWidget.value = updatedWidget;
};

const deleteWidget = () => {
  if (!selectedWidgetPath.value) return;
  const { rowIndex, colIndex, widgetIndex } = selectedWidgetPath.value;
  dashboardConfig.value.rows[rowIndex].columns[colIndex].widgets.splice(
    widgetIndex,
    1
  );
  deselectWidget();
};

const saveDashboard = async () => {
  saving.value = true;
  try {
    const updatedConfig = {
      ...portal.value.config,
      dashboard: dashboardConfig.value,
    };
    await ClientPortalsAPI.update(portal.value.id, {
      client_portal: { config: updatedConfig },
    });
    useAlert('Dashboard enregistré avec succès');
  } catch (error) {
    useAlert("Erreur lors de l'enregistrement");
  } finally {
    saving.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'client_portals_index' });
};

const togglePreview = () => {
  previewMode.value = !previewMode.value;
  if (previewMode.value) {
    deselectWidget();
  }
};

const headerTitle = computed(() => {
  if (previewMode.value) {
    return `Prévisualisation - ${portal.value?.name || ''}`;
  }
  return `Éditeur - ${portal.value?.name || ''}`;
});

// Fonction pour obtenir le style de grille responsive
const getGridStyle = columnCount => {
  // Sur mobile (< 768px), toujours 1 colonne (géré par grid-cols-1)
  // Sur desktop (>= 768px), utiliser le nombre de colonnes configuré
  return {
    '--column-count': columnCount,
  };
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Header -->
    <header class="sticky top-0 z-10 bg-n-background border-b border-n-weak">
      <div
        class="flex flex-col sm:flex-row sm:items-center justify-between w-full py-4 sm:py-6 px-4 sm:px-6 gap-3 sm:gap-2"
      >
        <span class="text-lg sm:text-xl font-medium truncate text-n-slate-12">
          {{ headerTitle }}
        </span>
        <div class="flex items-center flex-shrink-0 gap-2 sm:gap-4 flex-wrap">
          <Button
            :label="previewMode ? 'Mode Édition' : 'Prévisualisation'"
            :icon="previewMode ? 'i-lucide-edit' : 'i-lucide-eye'"
            variant="outline"
            color="slate"
            size="sm"
            class="text-xs sm:text-sm"
            @click="togglePreview"
          />
          <Button
            label="Retour"
            icon="i-lucide-arrow-left"
            variant="outline"
            color="slate"
            size="sm"
            class="text-xs sm:text-sm"
            @click="goBack"
          />
          <Button
            label="Enregistrer"
            icon="i-lucide-save"
            :loading="saving"
            size="sm"
            class="text-xs sm:text-sm"
            @click="saveDashboard"
          />
        </div>
      </div>
    </header>

    <!-- Main content -->
    <main class="flex-1 overflow-hidden">
      <div v-if="loading" class="flex items-center justify-center py-20 h-full">
        <Spinner />
      </div>

      <!-- Preview Mode -->
      <div v-else-if="previewMode" class="bg-n-alpha-1 h-full overflow-y-auto">
        <div
          class="w-full max-w-[1600px] mx-auto px-4 sm:px-5 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 lg:py-10"
        >
          <!-- Dashboard Header -->
          <div
            class="mb-4 sm:mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
          >
            <div>
              <h1
                class="text-xl sm:text-2xl font-semibold text-n-slate-12 mb-1"
              >
                {{ portal?.name }}
              </h1>
              <div
                class="flex items-center gap-2 text-xs sm:text-sm text-n-slate-10"
              >
                <span class="i-lucide-layout-dashboard size-3 sm:size-4" />
                <span>Dashboard Overview</span>
                <span class="mx-1">•</span>
                <a
                  :href="`/portal/${portal?.slug}`"
                  target="_blank"
                  class="flex items-center gap-1 text-n-blue-11 hover:underline"
                >
                  <span class="i-lucide-external-link size-3" />
                  Voir le portail public
                </a>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <span class="text-xs sm:text-sm text-n-slate-10">Last updated: Just now</span>
            </div>
          </div>

          <!-- Dashboard Content -->
          <div class="space-y-4 sm:space-y-6 md:space-y-8 lg:space-y-10">
            <div
              v-for="(row, rowIndex) in dashboardConfig.rows"
              :key="row.id"
              class="preview-row"
              :class="{
                'pb-4 sm:pb-6 md:pb-8 lg:pb-10 border-b border-n-weak/30':
                  rowIndex < dashboardConfig.rows.length - 1,
              }"
            >
              <div
                class="grid grid-cols-1 gap-3 sm:gap-4 md:gap-5 lg:gap-6 preview-grid"
                :style="getGridStyle(row.columns.length)"
                :data-columns="row.columns.length"
              >
                <div
                  v-for="(col, colIndex) in row.columns"
                  :key="col.id"
                  class="min-w-0 flex flex-col gap-3 sm:gap-4 md:gap-5 lg:gap-6 preview-col"
                  :class="{
                    'md:border-r md:border-n-weak/20 md:pr-3 lg:pr-4 xl:pr-5':
                      colIndex < row.columns.length - 1 &&
                      row.columns.length > 1,
                  }"
                >
                  <div
                    v-for="(widget, widgetIndex) in col.widgets"
                    :key="widget.id"
                    class="flex-1"
                  >
                    <div
                      class="h-full bg-n-solid-1 border border-n-weak/50 rounded-xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-sm hover:shadow-md hover:border-n-weak transition-all duration-300"
                    >
                      <WidgetRenderer :widget="widget" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-if="
                dashboardConfig.rows.length === 0 ||
                dashboardConfig.rows.every(r =>
                  r.columns.every(c => c.widgets.length === 0)
                )
              "
              class="flex flex-col items-center justify-center py-16 sm:py-24 md:py-32 text-center px-4"
            >
              <div
                class="p-6 sm:p-8 rounded-2xl bg-n-solid-1 border border-n-weak shadow-sm mb-4 sm:mb-6"
              >
                <span
                  class="i-lucide-layout-dashboard size-10 sm:size-12 text-n-slate-9"
                />
              </div>
              <h3 class="text-base sm:text-lg font-medium text-n-slate-12 mb-2">
                Dashboard vide
              </h3>
              <p class="text-xs sm:text-sm text-n-slate-10 max-w-sm">
                Ce dashboard est actuellement vide. Passez en mode édition pour
                commencer à construire votre vue.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Edit Mode -->
      <div
        v-else
        class="flex flex-col md:flex-row gap-0 h-full w-full overflow-hidden"
      >
        <!-- Sidebar Widgets -->
        <aside
          class="w-full md:w-64 lg:w-80 flex-shrink-0 bg-n-solid-1 border-r border-n-weak p-0 flex flex-col h-full md:h-auto z-10 order-2 md:order-1"
        >
          <div
            class="p-3 sm:p-4 md:p-5 border-b border-n-weak bg-n-solid-1 sticky top-0 z-10"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Widgets</h3>
            <p class="text-xs text-n-slate-10">
              Glissez les éléments sur la grille
            </p>
          </div>

          <div
            class="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 space-y-2 sm:space-y-3"
          >
            <draggable
              :list="widgetTypes"
              :group="{ name: 'widgets', pull: 'clone', put: false }"
              item-key="type"
              :clone="
                original => ({
                  ...original,
                  id: generateId(),
                  props: JSON.parse(JSON.stringify(original.defaultProps)),
                })
              "
              class="space-y-3"
            >
              <template #item="{ element }">
                <div
                  class="p-2 sm:p-3 bg-n-background border border-n-weak rounded-xl cursor-move hover:border-n-blue-9 hover:shadow-md transition-all group select-none"
                >
                  <div class="flex items-center gap-2 sm:gap-3">
                    <div
                      class="p-1.5 sm:p-2 bg-n-alpha-2 rounded-lg group-hover:bg-n-blue-2 group-hover:text-n-blue-11 transition-colors flex-shrink-0"
                    >
                      <span
                        :class="element.icon"
                        class="size-3 sm:size-4 text-n-slate-11 group-hover:text-n-blue-11"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div
                        class="text-xs sm:text-sm font-medium text-n-slate-12"
                      >
                        {{ element.label }}
                      </div>
                    </div>
                    <div
                      class="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <span
                        class="i-lucide-grip-vertical size-3 sm:size-4 text-n-slate-9"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </aside>

        <!-- Canvas -->
        <main
          class="flex-1 min-w-0 bg-n-alpha-1 overflow-y-auto p-4 sm:p-5 md:p-6 lg:p-8 order-1 md:order-2"
        >
          <div
            class="w-full max-w-[1600px] mx-auto space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8"
          >
            <draggable
              v-model="dashboardConfig.rows"
              group="rows"
              item-key="id"
              handle=".row-handle"
              class="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8"
            >
              <template #item="{ element: row, index: rowIndex }">
                <div
                  class="relative group/row bg-n-background border border-n-weak rounded-xl p-4 sm:p-5 md:p-6 lg:p-7 shadow-sm hover:border-n-blue-6/50 hover:shadow-md transition-all"
                  :class="{
                    'mb-4 sm:mb-5 md:mb-6 lg:mb-8':
                      rowIndex < dashboardConfig.rows.length - 1,
                  }"
                >
                  <!-- Row Controls -->
                  <div
                    class="absolute -top-2 sm:-top-2.5 md:-top-3 left-2 sm:left-3 md:left-4 flex gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity z-10"
                  >
                    <button
                      class="row-handle p-1 sm:p-1.5 bg-n-slate-12 text-white rounded-md text-xs flex items-center gap-1 cursor-move shadow-sm hover:bg-n-slate-11"
                      title="Déplacer la rangée"
                    >
                      <span class="i-lucide-grip-vertical size-3" />
                    </button>
                    <button
                      class="p-1 sm:p-1.5 bg-n-ruby-9 text-white rounded-md text-xs shadow-sm hover:bg-n-ruby-10"
                      title="Supprimer la rangée"
                      @click="removeRow(rowIndex)"
                    >
                      <span class="i-lucide-trash-2 size-3" />
                    </button>
                  </div>

                  <!-- Row Label -->
                  <div
                    class="mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-n-weak/30"
                  >
                    <span
                      class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
                    >
                      Section {{ rowIndex + 1 }}
                    </span>
                  </div>

                  <!-- Columns Grid -->
                  <div
                    class="flex flex-col sm:flex-row gap-4 sm:gap-5 md:gap-6 mt-3 sm:mt-4"
                  >
                    <div
                      v-for="(col, colIndex) in row.columns"
                      :key="col.id"
                      class="flex-1 min-w-0 relative group/col flex flex-col edit-col"
                      :style="{ flex: `0 0 ${(col.width / 12) * 100}%` }"
                      :class="{
                        'md:border-r md:border-n-weak/30 md:pr-4 lg:pr-5 xl:pr-6':
                          colIndex < row.columns.length - 1 &&
                          row.columns.length > 1,
                      }"
                    >
                      <!-- Column Label -->
                      <div
                        class="mb-2 sm:mb-3 opacity-0 group-hover/col:opacity-100 transition-opacity"
                      >
                        <span class="text-xs text-n-slate-9 font-medium">
                          Colonne {{ colIndex + 1 }} ({{
                            Math.round((col.width / 12) * 100)
                          }}%)
                        </span>
                      </div>
                      <div
                        class="flex-1 min-h-[120px] sm:min-h-[150px] md:min-h-[180px] lg:min-h-[200px] p-3 sm:p-4 md:p-5 rounded-xl border-2 border-dashed border-n-weak bg-n-alpha-1/50 group-hover/col:border-n-blue-6/30 group-hover/col:bg-n-blue-1/5 transition-all"
                      >
                        <draggable
                          v-model="col.widgets"
                          group="widgets"
                          item-key="id"
                          class="h-full space-y-3 sm:space-y-4"
                        >
                          <template
                            #item="{ element: widget, index: widgetIndex }"
                          >
                            <div
                              class="bg-n-solid-1 border rounded-xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-sm cursor-pointer transition-all hover:shadow-md group/widget relative overflow-hidden"
                              :class="
                                selectedWidget?.id === widget.id
                                  ? 'ring-2 ring-n-blue-9 border-n-blue-9'
                                  : 'border-n-weak hover:border-n-blue-6/50'
                              "
                              @click="
                                selectWidget(
                                  widget,
                                  rowIndex,
                                  colIndex,
                                  widgetIndex
                                )
                              "
                            >
                              <div
                                class="min-h-[60px] sm:min-h-[80px] md:min-h-[100px]"
                              >
                                <WidgetRenderer :widget="widget" />
                              </div>

                              <!-- Widget Overlay Actions -->
                              <div
                                class="absolute top-2 right-2 opacity-0 group-hover/widget:opacity-100 transition-opacity flex gap-1"
                              >
                                <button
                                  class="p-1.5 rounded-md bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-11"
                                  title="Éditer"
                                >
                                  <span class="i-lucide-settings-2 size-4" />
                                </button>
                              </div>
                            </div>
                          </template>
                          <template v-if="col.widgets.length === 0" #header>
                            <div
                              class="flex flex-col items-center justify-center h-full text-n-slate-9 py-8 sm:py-10 md:py-12"
                            >
                              <span
                                class="i-lucide-plus size-6 sm:size-8 md:size-10 mb-2 opacity-50"
                              />
                              <span class="text-xs font-medium">Déposez un widget</span>
                            </div>
                          </template>
                        </draggable>
                      </div>

                      <button
                        v-if="row.columns.length > 1"
                        class="absolute -bottom-5 sm:-bottom-6 md:-bottom-7 left-1/2 -translate-x-1/2 opacity-0 group-hover/col:opacity-100 text-xs text-n-ruby-11 hover:text-n-ruby-12 font-medium transition-opacity bg-n-background px-2.5 py-1 rounded border border-n-weak shadow-sm mt-2"
                        @click="removeColumn(row, colIndex)"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <!-- Add Column Button -->
                  <div
                    class="flex justify-center mt-4 sm:mt-5 md:mt-6 pt-3 sm:pt-4 md:pt-5 border-t border-n-weak border-dashed opacity-0 group-hover/row:opacity-100 transition-opacity"
                  >
                    <button
                      class="text-xs font-medium text-n-slate-10 hover:text-n-blue-11 hover:underline flex items-center gap-1.5 transition-colors"
                      @click="addColumn(row)"
                    >
                      <span class="i-lucide-columns size-3 sm:size-3.5" />
                      Ajouter une colonne
                    </button>
                  </div>
                </div>
              </template>
            </draggable>

            <!-- Add Row Button -->
            <button
              class="w-full py-4 sm:py-6 md:py-8 border-2 border-dashed border-n-weak rounded-xl hover:bg-n-alpha-1 hover:border-n-slate-400 transition-all flex flex-col items-center gap-2 text-n-slate-9 hover:text-n-slate-11 group/add-row"
              @click="addRow"
            >
              <div
                class="p-1.5 sm:p-2 rounded-full bg-n-alpha-2 group-hover/add-row:bg-n-alpha-3 transition-colors"
              >
                <span class="i-lucide-plus size-4 sm:size-5" />
              </div>
              <span class="text-xs sm:text-sm font-medium">Ajouter une section</span>
            </button>
          </div>
        </main>

        <!-- Properties Panel -->
        <WidgetPropertiesPanel
          v-if="showProperties && selectedWidget"
          :widget="selectedWidget"
          @update="updateWidget"
          @close="deselectWidget"
          @delete="deleteWidget"
        />
      </div>
    </main>
  </div>
</template>

<style scoped>
.ghost {
  @apply opacity-50 bg-n-blue-2 border-dashed border-n-blue-9;
}

/* Grille responsive pour le preview */
.preview-grid {
  grid-template-columns: repeat(1, 1fr);
}

@media (min-width: 768px) {
  .preview-grid[data-columns='1'] {
    grid-template-columns: repeat(1, 1fr);
  }
  .preview-grid[data-columns='2'] {
    grid-template-columns: repeat(2, 1fr);
  }
  .preview-grid[data-columns='3'] {
    grid-template-columns: repeat(3, 1fr);
  }
  .preview-grid[data-columns='4'] {
    grid-template-columns: repeat(4, 1fr);
  }
  .preview-grid[data-columns='5'],
  .preview-grid[data-columns='6'] {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1024px) {
  .preview-grid[data-columns='3'] {
    grid-template-columns: repeat(3, 1fr);
  }
  .preview-grid[data-columns='4'] {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 1280px) {
  .preview-grid[data-columns='4'] {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
